<!-- 
Table for displaying and editing risk factor ranges.
-->
<template>
  <v-card :elevation="(editing ? 12 : 0)" flat class="mr-2">
    <v-card-title v-if="!hideTitle" primary-title> 
      <h3 class="subheading mb-0">
        {{ title }}
      </h3>
    </v-card-title>
    <v-card-text class="pr-2 mr-2 pt-0">
      <v-data-table 
        :headers="headers" 
        :items="factors" 
        :total-items="factors.length" 
        :loading="state.loading" 
        hide-actions 
        class="risk-factor-table"
      >
        <template slot="items" slot-scope="props">
          <tr class="noselect">
            <td>
              {{ getBoundDescription(props.item) }}
            </td>
            <td class="text-xs-center">
              {{ props.item.score | numericInput2 }}
            </td>
            <td v-if="editing">
              <v-tooltip left>
                <v-btn 
                  slot="activator" 
                  small 
                  icon 
                  class="ma-0 primary--text edit-risk-factor-row-button" 
                  @click.prevent="edit(props.item)"
                >
                  <v-icon small>edit</v-icon>
                </v-btn>
                <span>Edit this range</span>
              </v-tooltip>
              <v-tooltip right>
                <v-btn
                  slot="activator" 
                  small
                  icon
                  class="ma-0 error--text delete-risk-factor-row-button" 
                  @click.prevent="del(props.item)"
                >
                  <v-icon small>clear</v-icon>
                </v-btn>
                <span>Delete this range</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
    <v-card-actions v-if="canEdit && !editing" class="justify-center">
      <v-btn class="edit-risk-factor-table-button" @click="$emit('update:editing',true)">
        <v-icon class="mr-2">edit</v-icon>
        Edit
      </v-btn>
    </v-card-actions>
    <v-card-actions v-if="editing">
      <v-btn color="primary" flat @click="showCreateDialog=true">
        Add new range
      </v-btn>
      <v-spacer/>
      <v-btn color="primary" flat @click="$emit('update:editing', false)">
        Done
      </v-btn>
    </v-card-actions>
    <risk-factor-edit-dialog 
      v-model="showEditDialog" 
      :levy-collection-id="levyCollectionId" 
      :risk-factor-id="editRiskFactorId"
      :risk-factor-type="type" 
      :lower-bound-label="tableTitle ? tableTitle : title"
      :terminology="terminology"
    />
    <risk-factor-edit-dialog 
      v-model="showCreateDialog" 
      :levy-collection-id="levyCollectionId" 
      :risk-factor-type="type" 
      :lower-bound-label="tableTitle ? tableTitle : title"
      :terminology="terminology"
    />
    <risk-factor-delete-dialog
      v-model="showDeleteDialog"
      :levy-collection-id="levyCollectionId"
      :risk-factor-id="deleteRiskFactorId"
    />
  </v-card>
</template>

<script>
import RiskFactorEditDialog from "@/components/levy-collection-risk-factors/RiskFactorEditDialog.vue";
import RiskFactorDeleteDialog from "@/components/levy-collection-risk-factors/RiskFactorDeleteDialog.vue";
import * as claim from "@/store/claim-types";
import * as action from "@/store/levy-collection-risk-factors/action-types";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import * as formatting from "@/helpers/formatting.helper";

export default {
  components: { RiskFactorEditDialog, RiskFactorDeleteDialog },
  mixins: [filterMixins, lookupMixins],
  props: {
    editing: {
      type: Boolean,
      required: true
    },
    type: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    terminology: {
      type: String,
      default: "Value"
    },
    hideTitle: {
      type: Boolean,
      default: false
    },
    tableTitle: {
      type: String,
      default: null
    },
    levyCollectionId: {
      validator: prop => typeof prop === "number" || prop === null,
      required: true
    },
    isCurrency: {
      type: Boolean,
      default: false
    },
    isPercentage: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showEditDialog: false,
      showCreateDialog: false,
      showDeleteDialog: false,
      editRiskFactorId: null,
      deleteRiskFactorId: null
    };
  },
  computed: {
    headers() {
      const h = [
        { text: this.tableTitle ? this.tableTitle : this.title, align: "left", sortable: false, value: "range" },
        { text: this.terminology, align: "center", sortable: false, value: "score" }
      ];
      if (this.editing) {
        h.push({ text: "", value: "Id", sortable: false, align: "right", width: 110 });
      }
      return h;
    },
    state() {
      return this.$store.state.levyCollectionRiskFactors.listing;
    },
    loaded() {
      return this.state.loaded;
    },
    factors() {
      return this.state.data.filter(r => r.type == this.type);
    },
    canEdit() {
      const hasPermission = this.$store.getters.claims.includes(claim.LEVY_COLLECTION_CONFIGURE_RISK_FACTORS);
      const status = this.$store.state.levyCollections.view.data.status;
      const isAllowableStatus = this.doesLookupHaveAttribute(
        status,
        "CanEditRiskFactors",
        this.lookups.levyCollectionStatuses
      );
      return hasPermission && isAllowableStatus;
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      if (this.levyCollectionId) {
        const isDifferentLevyCollection = this.state.levyCollectionId != this.levyCollectionId;
        if ((!this.state.loaded && !this.state.loading) || isDifferentLevyCollection) {
          this.$store.dispatch(action.VIEW, this.levyCollectionId);
        }
      }
    },
    getBoundDescription(riskFactor) {
      const description = formatting.getRiskFactorLowerBoundDescription(
        riskFactor,
        this.factors,
        this.getLookup(this.type, this.lookups.riskFactorTypes)
      );
      return riskFactor.customLabel ? riskFactor.customLabel : description;
    },
    edit(riskFactor) {
      this.editRiskFactorId = riskFactor.id;
      this.showEditDialog = true;
    },
    del(riskFactor) {
      this.deleteRiskFactorId = riskFactor.id;
      this.showDeleteDialog = true;
    }
  }
};
</script>

<style lang="scss">
table.v-table tbody {
  tr.highlight,
  tr.highlight:hover {
    background-color: #f8f8f8;
  }
  td.highlight {
    font-weight: bold;
  }
}
.risk-factor-table table {
  border: 1px solid #f0f0f0;
}
</style>